import { useState } from "react";
import PlayerAvatar from "../common/PlayerAvatar";
import ClubCrest from "../common/ClubCrest";
import { formatGroupedNumber } from "../../utils/currencyFormatter";

// Compare page — transfer history as per-player columns below the grid
// (frame 1g). Full club names with small crests in front (endpoint fields
// from_team_image_url/to_team_image_url; fallback = the existing ClubCrest
// shield, no new asset sources) — truncation only under real space pressure,
// with a title tooltip; fee pill (reported fees darker, unknown/promoted
// lighter); NO market-value column anywhere. Sorted newest first (the data
// hook already sorts, pattern from search/PlayerTransfers). Mobile:
// collapsed by default.

const feeText = (transfer) => {
  const amount = transfer?.fee_amount;
  if (amount && amount > 0) {
    const cur = (transfer.fee_currency || "EUR").toUpperCase();
    const prefix = cur === "EUR" ? "€" : cur === "USD" ? "$" : cur === "GBP" ? "£" : `${cur} `;
    if (amount >= 1_000_000) return { text: `${prefix}${(amount / 1_000_000).toFixed(1)}m`, known: true };
    if (amount >= 1_000) return { text: `${prefix}${(amount / 1_000).toFixed(0)}K`, known: true };
    return { text: `${prefix}${formatGroupedNumber(amount)}`, known: true };
  }
  const type = String(transfer?.type || "").trim();
  if (type) {
    const label = type.replace(/[-_]+/g, " ");
    const pretty = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
    return { text: pretty, known: /free/i.test(type) };
  }
  return { text: "Undisclosed", known: false };
};

const dateLabel = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const MoveRow = ({ move }) => {
  const fee = feeText(move);
  return (
    <div className="grid grid-cols-[78px_minmax(0,1fr)_auto] items-center gap-3 py-[10px] border-t border-[#F4F7FA]">
      <div className="text-[11.5px] font-semibold text-[#9AA7B8] tabular-nums" data-no-translate="true">
        {dateLabel(move.date)}
      </div>
      <div className="flex items-center gap-2 min-w-0">
        {move.from_team && (
          <ClubCrest
            name={move.from_team}
            logoUrl={move.from_team_image_url || null}
            width={14}
            height={16}
          />
        )}
        <span
          className="text-[12.5px] text-[#64748B] truncate"
          title={move.from_team || ""}
          data-no-translate="true"
        >
          {move.from_team || "—"}
        </span>
        <span className="text-[11px] text-[#C3CDDB] flex-shrink-0">→</span>
        {move.to_team && (
          <ClubCrest
            name={move.to_team}
            logoUrl={move.to_team_image_url || null}
            width={14}
            height={16}
          />
        )}
        <span
          className="text-[12.5px] font-semibold text-[#0F172A] truncate"
          title={move.to_team || ""}
          data-no-translate="true"
        >
          {move.to_team || "—"}
        </span>
      </div>
      <span
        className="px-2 py-[3px] rounded-md text-[10.5px] font-bold whitespace-nowrap"
        style={
          fee.known
            ? { background: "#F1F4F9", color: "#4B5A6E" }
            : { background: "#F7F9FC", color: "#9AA7B8" }
        }
      >
        {fee.text}
      </span>
    </div>
  );
};

const PlayerColumn = ({ player, transfers }) => (
  <div className="px-[18px] py-4 border-r border-[#F1F5F9] last:border-r-0 flex flex-col gap-[14px] min-w-0">
    <div className="flex items-center gap-[9px]">
      <PlayerAvatar src={player.imageUrl} name={player.name} className="w-7 h-7 rounded-full text-[10.5px] flex-shrink-0" />
      <div className="text-[13.5px] font-semibold text-[#0F172A] truncate" data-no-translate="true">
        {player.name}
      </div>
      <span className="text-[11px] text-[#9AA7B8] whitespace-nowrap">
        {transfers.length === 1 ? "1 move" : `${transfers.length} moves`}
      </span>
    </div>
    <div className="flex flex-col">
      {transfers.length ? (
        transfers.map((m, i) => <MoveRow key={m.transfer_id || i} move={m} />)
      ) : (
        <div className="py-[10px] border-t border-[#F4F7FA] text-[12px] text-[#8494A8]">
          No transfers recorded
        </div>
      )}
    </div>
  </div>
);

const TransferHistory = ({ columns, variant = "desktop" }) => {
  const [collapsed, setCollapsed] = useState(variant === "mobile");
  if (!columns || !columns.length) return null;

  if (variant === "mobile") {
    return (
      <div className="bg-white border border-[#E4E9F0] rounded-[14px] overflow-hidden">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="w-full flex items-center justify-between px-[13px] py-[14px] text-left"
        >
          <span className="flex flex-col gap-[2px]">
            <span className="text-[13px] font-semibold text-[#0F172A]">Transfer history</span>
            <span className="text-[11px] text-[#8494A8]">Clubs, dates and known fees</span>
          </span>
          <span className={`text-[9px] text-[#7A8798] transition-transform ${collapsed ? "" : "rotate-180"}`}>▼</span>
        </button>
        {!collapsed && (
          <div className="border-t border-[#EDF1F6]">
            {columns.map(({ player, transfers }) => (
              <div key={player.id} className="border-b border-[#F1F5F9] last:border-b-0">
                <PlayerColumn player={player} transfers={transfers} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E4E9F0] rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,.04)]">
      <div className="flex items-center justify-between px-[18px] py-[15px] border-b border-[#EDF1F6]">
        <div className="flex flex-col gap-[2px]">
          <div className="text-[15px] font-semibold text-[#0F172A]">Transfer history</div>
          <div className="text-[11.5px] text-[#7A8798]">Full club names · fee shown when reported</div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="text-[11.5px] font-semibold text-[#1B6FD6] hover:text-[#0F5FBF]"
        >
          {collapsed ? "Expand" : "Collapse"}
        </button>
      </div>
      {!collapsed && (
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))` }}
        >
          {columns.map(({ player, transfers }) => (
            <PlayerColumn key={player.id} player={player} transfers={transfers} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TransferHistory;
